<template>
  <div class="date-picker">
  <PvDatePicker
    ref="picker"
    fluid
    :model-value="internalDate"
    :input-id="inputId"
    :name="name"
    :input-class="mergedInputClass"
    :placeholder="placeholder"
    :disabled="isDisabled"
    :readonly="pickerReadonly"
    :required="isRequired"
    :show-time="showTime"
    :time-only="timeOnly"
    hour-format="24"
    :step-minute="stepMinute"
    :min-date="stableMinDate"
    :date-format="primeDateFormat"
    :manual-input="allowManualInput"
    select-other-months
    panel-class="wsd-datepicker-panel"
    show-icon
    icon-display="input"
    :hide-on-date-time-select="false"
    @update:model-value="onModelUpdate"
    @input="onManualInput"
    @blur="onInputBlur"
    @show="onPickerShow"
    @hide="onPickerHide"
  >
    <template #inputicon="{ clickCallback }">
      <span
        class="p-datepicker-input-icon-trigger"
        role="button"
        tabindex="-1"
        @click="clickCallback"
      >
        <i :class="[resolvedPickerIcon, 'p-datepicker-input-icon']" />
      </span>
    </template>
    <template v-if="hasTimeSelect" #footer>
      <div class="wsd-time-select" @mousedown.prevent>
        <div class="wsd-time-select__display" role="group" aria-label="Time">
          <button
            type="button"
            class="wsd-time-select__part"
            :class="{ 'is-active': timeSelectStep === 'hour' }"
            :aria-pressed="timeSelectStep === 'hour'"
            @click="openHourSelect"
          >
            {{ paddedHour }}
          </button>
          <span class="wsd-time-select__sep" aria-hidden="true">:</span>
          <button
            type="button"
            class="wsd-time-select__part"
            :class="{ 'is-active': timeSelectStep === 'minute' }"
            :aria-pressed="timeSelectStep === 'minute'"
            @click="openMinuteSelect"
          >
            {{ paddedMinute }}
          </button>
        </div>

        <div v-if="timeSelectStep === 'hour'" class="wsd-time-select__panel">
          <div class="wsd-time-select__grid wsd-time-select__grid--hours" role="listbox" aria-label="Hour">
            <button
              v-for="hour in hourOptions"
              :key="`h-${hour}`"
              type="button"
              class="wsd-time-select__option"
              :class="{ 'is-selected': hour === selectedHour }"
              role="option"
              :aria-selected="hour === selectedHour"
              @click="selectHour(hour)"
            >
              {{ padTimeUnit(hour) }}
            </button>
          </div>
        </div>

        <div v-else-if="timeSelectStep === 'minute'" class="wsd-time-select__panel">
          <div class="wsd-time-select__grid wsd-time-select__grid--minutes" role="listbox" aria-label="Minute">
            <button
              v-for="minute in minuteOptions"
              :key="`m-${minute}`"
              type="button"
              class="wsd-time-select__option"
              :class="{ 'is-selected': minute === selectedMinute }"
              role="option"
              :aria-selected="minute === selectedMinute"
              @click="selectMinute(minute)"
            >
              {{ padTimeUnit(minute) }}
            </button>
          </div>
        </div>
      </div>
    </template>
  </PvDatePicker>
  </div>
</template>

<script>
import PvDatePicker from 'primevue/datepicker'
import { resolveLocaleString } from '../../utils/locale'
import { momentFormatToPrimeVue, isTimeOnlyFormat, formatShowsTime } from '../../utils/moment-to-prime-format'
import { keepDateIfUnchanged } from '../../utils/dateUtils'

export default {
  name: 'PrimeVueDatePicker',
  components: { PvDatePicker },
  inheritAttrs: false,
  props: {
    modelValue: {
      default: null
    },
    value: {
      default: null
    },
    config: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: [Boolean, String],
      default: false
    },
    disabled: {
      type: [Boolean, String],
      default: false
    }
  },
  emits: ['update:modelValue', 'dp-change', 'blur'],
  data () {
    return {
      internalDate: null,
      minDateOverride: undefined,
      stableMinDate: null,
      timeSelectStep: null,
      manualInputDirty: false
    }
  },
  computed: {
    boundValue () {
      return this.modelValue !== undefined && this.modelValue !== null
        ? this.modelValue
        : this.value
    },
    inputId () {
      return this.$attrs.id || this.$attrs.name || `date-picker-${this.$.uid}`
    },
    name () {
      return this.$attrs.name
    },
    placeholder () {
      return this.$attrs.placeholder
    },
    isRequired () {
      return this.$attrs.required !== undefined && this.$attrs.required !== false
    },
    mergedInputClass () {
      const cls = this.$attrs.class
      if (!cls) {
        return 'form-control'
      }
      if (typeof cls === 'string') {
        return cls
      }
      if (Array.isArray(cls)) {
        return cls.filter(Boolean).join(' ')
      }
      return 'form-control'
    },
    isDisabled () {
      return this.disabled === true || this.disabled === 'true' || this.disabled === ''
    },
    isReadonly () {
      return this.readonly === true || this.readonly === 'true' || this.readonly === ''
    },
    allowManualInput () {
      if (this.isReadonly) {
        return false
      }
      return this.config.ignoreReadonly !== false
    },
    configFormat () {
      return this.config.format || ''
    },
    timeOnly () {
      return this.config.timeOnly === true || isTimeOnlyFormat(this.configFormat)
    },
    showTime () {
      return !this.timeOnly && formatShowsTime(this.configFormat)
    },
    hasTimeSelect () {
      return this.showTime || this.timeOnly
    },
    stepMinute () {
      return this.config.stepping || 1
    },
    primeDateFormat () {
      const converted = momentFormatToPrimeVue(this.configFormat, { dateOnly: this.showTime })
      if (converted) {
        return converted
      }
      if (this.timeOnly) {
        return undefined
      }
      return this.showTime ? 'dd/mm/yy' : undefined
    },
    minDateSource () {
      return this.minDateOverride !== undefined
        ? this.minDateOverride
        : this.config.minDate
    },
    pickerIcon () {
      if (this.timeOnly) {
        return this.config.icons?.time || 'fal fa-clock'
      }
      return this.config.icons?.date || undefined
    },
    resolvedPickerIcon () {
      return this.pickerIcon || (this.timeOnly ? 'fal fa-clock' : 'fal fa-calendar')
    },
    pickerReadonly () {
      if (!this.isReadonly) {
        return false
      }
      // PrimeVue disables icon clicks when readonly is true. When ignoreReadonly is
      // enabled, block typing via manualInput but keep the picker interactive.
      return this.config.ignoreReadonly === false
    },
    selectedHour () {
      return this.currentTimeParts().hour
    },
    selectedMinute () {
      return this.currentTimeParts().minute
    },
    paddedHour () {
      return this.padTimeUnit(this.selectedHour)
    },
    paddedMinute () {
      return this.padTimeUnit(this.selectedMinute)
    },
    hourOptions () {
      return Array.from({ length: 24 }, (_, hour) => hour)
    },
    minuteOptions () {
      const step = Math.max(1, Number(this.stepMinute) || 1)
      const options = []
      for (let minute = 0; minute < 60; minute += step) {
        options.push(minute)
      }
      if (!options.includes(this.selectedMinute)) {
        options.push(this.selectedMinute)
        options.sort((a, b) => a - b)
      }
      return options
    }
  },
  watch: {
    boundValue: {
      handler (next) {
        this.syncFromBoundValue(next)
      },
      immediate: false
    },
    minDateSource: {
      immediate: true,
      handler (source) {
        const next = this.toDate(source)
        if (keepDateIfUnchanged(this.stableMinDate, next) === this.stableMinDate) {
          return
        }
        this.preserveOpenPickerMonth(() => {
          this.stableMinDate = next
        })
      }
    },
    config: {
      deep: true,
      handler () {
        this.applyLocale()
      }
    }
  },
  mounted () {
    this.applyLocale()
    this.syncFromBoundValue(this.boundValue)
    this.attachJqueryShim()
  },
  updated () {
    this.attachJqueryShim()
  },
  beforeUnmount () {
    this.detachJqueryShim()
  },
  methods: {
    applyLocale () {
      const locale = resolveLocaleString(this.config.locale)
      if (locale && typeof this.moment?.locale === 'function') {
        this.moment.locale(locale)
      }
    },
    toDate (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const m = this.moment.isMoment(value) ? value : this.moment(value)
      return m.isValid() ? m.toDate() : null
    },
    toMoment (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const m = this.moment.isMoment(value) ? value.clone() : this.moment(value)
      return m.isValid() ? m : null
    },
    currentMoment () {
      return this.toMoment(this.internalDate)
    },
    minDateMoment () {
      return this.toMoment(this.minDateSource)
    },
    syncFromBoundValue (value = this.boundValue) {
      if (this.manualInputDirty) {
        return
      }
      this.internalDate = keepDateIfUnchanged(this.internalDate, this.toDate(value))
    },
    setDate (value) {
      if (this.manualInputDirty) {
        return this.currentMoment()
      }
      const oldMoment = this.currentMoment()
      if (value === null || value === undefined || value === '') {
        this.internalDate = null
        this.$emit('update:modelValue', null)
        return oldMoment
      }
      const next = this.toDate(value)
      this.internalDate = next
      const nextMoment = this.currentMoment()
      this.$emit('update:modelValue', nextMoment)
      return oldMoment
    },
    setMinDate (value) {
      this.minDateOverride = value === null || value === undefined ? null : this.toMoment(value) || value
    },
    clearDate () {
      this.setDate(null)
    },
    getInputElement () {
      if (this.inputId) {
        const byId = document.getElementById(this.inputId)
        if (byId) {
          return byId
        }
      }
      return this.$refs.picker?.$el?.querySelector?.('input') ?? null
    },
    $jq () {
      const el = this.getInputElement()
      return el ? this.$jQuery(el) : this.$jQuery([])
    },
    createShim () {
      const vm = this
      const api = {
        date (value) {
          if (value === undefined) {
            return vm.currentMoment()
          }
          const oldMoment = vm.currentMoment()
          vm.setDate(value)
          const newMoment = vm.currentMoment()
          if ((oldMoment?.valueOf?.() ?? null) !== (newMoment?.valueOf?.() ?? null)) {
            vm.emitDpChange(newMoment, oldMoment)
          }
          return api
        },
        minDate (value) {
          if (value === undefined) {
            return vm.minDateMoment()
          }
          vm.setMinDate(value)
          return api
        },
        clear () {
          vm.clearDate()
          vm.emitDpChange(null, vm.currentMoment())
          return api
        },
        destroy () {}
      }
      return api
    },
    attachJqueryShim () {
      const el = this.getInputElement()
      if (!el || el.tagName !== 'INPUT') {
        return
      }
      this.$jQuery(el).data('DateTimePicker', this.createShim())
    },
    detachJqueryShim () {
      const $input = this.$jq()
      if (!$input.length) {
        return
      }
      // Do not $input.off('dp.change'): Appointment and others bind their own
      // dp.change listeners on this input. Removing them breaks start→end coupling.
      $input.removeData('DateTimePicker')
    },
    emitDpChange (date, oldDate) {
      const payload = { date, oldDate }
      this.$emit('dp-change', payload)
      const $input = this.$jq()
      if ($input.length) {
        $input.trigger(this.$jQuery.Event('dp.change', payload))
      }
    },
    /**
     * PrimeVue selectDate stamps currentHour/currentMinute (often 00:00, "now",
     * or minDate's clock) onto the clicked day. Calendar clicks must keep the
     * already chosen clock. applySelectedTime passes preserveClock: false.
     */
    withPreservedClock (value, previousMoment, preserveClock) {
      if (!preserveClock || !this.showTime || this.timeOnly || !value || !previousMoment) {
        return value
      }
      const nextDate = this.toDate(value)
      if (!nextDate) {
        return value
      }
      nextDate.setHours(previousMoment.hours(), previousMoment.minutes(), previousMoment.seconds(), 0)
      return nextDate
    },
    clampToMinDate (date) {
      const dateObj = this.toDate(date)
      const min = this.stableMinDate
      if (!dateObj || !min || dateObj.getTime() >= min.getTime()) {
        return dateObj ?? date
      }
      return new Date(min.getTime())
    },
    onModelUpdate (value, { preserveClock = true } = {}) {
      const oldMoment = this.currentMoment()
      const next = this.clampToMinDate(this.withPreservedClock(value, oldMoment, preserveClock))
      this.manualInputDirty = false
      this.internalDate = next
      const newMoment = this.currentMoment()
      this.$emit('update:modelValue', newMoment)
      this.emitDpChange(newMoment, oldMoment)
    },
    applyPickerMonth (month, year) {
      const picker = this.$refs.picker
      if (!picker || this.timeOnly || typeof month !== 'number' || typeof year !== 'number') {
        return
      }
      picker.currentMonth = month
      picker.currentYear = year
    },
    syncViewToSelectedDate () {
      const date = this.toDate(this.internalDate) || this.stableMinDate
      if (!date) {
        return
      }
      this.applyPickerMonth(date.getMonth(), date.getFullYear())
    },
    preserveOpenPickerMonth (update) {
      const picker = this.$refs.picker
      const month = picker?.currentMonth
      const year = picker?.currentYear
      const restore = picker?.overlayVisible && typeof month === 'number' && typeof year === 'number'
      update()
      if (restore) {
        this.$nextTick(() => this.applyPickerMonth(month, year))
      }
    },
    onManualInput () {
      if (!this.allowManualInput) {
        return
      }
      this.manualInputDirty = true
    },
    onInputBlur (event) {
      this.flushManualInput()
      this.$emit('blur', event)
    },
    flushManualInput () {
      if (!this.allowManualInput || !this.manualInputDirty) {
        return
      }
      const text = this.getInputElement()?.value
      this.manualInputDirty = false
      this.commitTypedValue(text, { syncInput: true })
    },
    parseTypedValue (text) {
      if (text === null || text === undefined) {
        return undefined
      }
      const trimmed = String(text).trim()
      if (!trimmed) {
        return null
      }
      const format = this.configFormat
      const parsed = format
        ? this.moment(trimmed, format, true)
        : this.moment(trimmed)
      return parsed.isValid() ? parsed : undefined
    },
    // PrimeVue 4.5.x throws while parsing 24-hour time, so typed values are parsed here.
    commitTypedValue (text, { syncInput = false } = {}) {
      const parsed = this.parseTypedValue(text)
      if (parsed === undefined) {
        if (syncInput) {
          this.$nextTick(() => this.syncInputDisplay())
        }
        return
      }

      const next = parsed === null ? null : this.clampToMinDate(parsed.toDate())
      const prevTime = this.internalDate?.getTime?.() ?? null
      const nextTime = next?.getTime?.() ?? null
      if (prevTime === nextTime) {
        if (syncInput) {
          this.$nextTick(() => this.syncInputDisplay())
        }
        return
      }

      const oldMoment = this.currentMoment()
      this.internalDate = next
      const newMoment = this.currentMoment()
      this.$emit('update:modelValue', newMoment)
      this.emitDpChange(newMoment, oldMoment)

      if (syncInput) {
        this.$nextTick(() => this.syncInputDisplay())
      }
    },
    syncInputDisplay () {
      const el = this.getInputElement()
      if (!el) {
        return
      }
      const current = this.currentMoment()
      if (!current) {
        el.value = ''
        return
      }
      const format = this.configFormat
      if (format) {
        el.value = current.format(format)
      }
    },
    padTimeUnit (value) {
      return String(value).padStart(2, '0')
    },
    currentTimeParts () {
      const source = this.internalDate instanceof Date && !Number.isNaN(this.internalDate.getTime())
        ? this.internalDate
        : (this.$refs.picker
            ? new Date(
              this.$refs.picker.currentYear ?? new Date().getFullYear(),
              this.$refs.picker.currentMonth ?? new Date().getMonth(),
              new Date().getDate(),
              this.$refs.picker.currentHour ?? new Date().getHours(),
              this.$refs.picker.currentMinute ?? new Date().getMinutes()
            )
            : new Date())
      return {
        hour: source.getHours(),
        minute: source.getMinutes()
      }
    },
    onPickerShow () {
      // Time-only pickers open on hour selection; datetime keeps the calendar primary.
      this.timeSelectStep = this.timeOnly ? 'hour' : null
      this.syncViewToSelectedDate()
      if (this.timeOnly) {
        this.$nextTick(() => this.scrollSelectedTimeOptionIntoView())
      }
    },
    onPickerHide () {
      this.timeSelectStep = null
      this.syncViewToSelectedDate()
    },
    openHourSelect () {
      this.timeSelectStep = this.timeSelectStep === 'hour' ? null : 'hour'
      this.$nextTick(() => this.scrollSelectedTimeOptionIntoView())
    },
    openMinuteSelect () {
      this.timeSelectStep = this.timeSelectStep === 'minute' ? null : 'minute'
      this.$nextTick(() => this.scrollSelectedTimeOptionIntoView())
    },
    selectHour (hour) {
      this.applySelectedTime({ hour })
      this.timeSelectStep = 'minute'
      this.$nextTick(() => this.scrollSelectedTimeOptionIntoView())
    },
    selectMinute (minute) {
      this.applySelectedTime({ minute })
      this.timeSelectStep = null
    },
    scrollSelectedTimeOptionIntoView () {
      const panel = document.querySelector('.wsd-datepicker-panel .wsd-time-select__option.is-selected')
      panel?.scrollIntoView?.({ block: 'nearest' })
    },
    applySelectedTime ({ hour, minute } = {}) {
      const base = this.internalDate instanceof Date && !Number.isNaN(this.internalDate.getTime())
        ? new Date(this.internalDate.getTime())
        : new Date()

      if (this.$refs.picker && !(this.internalDate instanceof Date)) {
        // Prefer the calendar's currently visible date when no value is set yet.
        if (typeof this.$refs.picker.currentYear === 'number') {
          base.setFullYear(this.$refs.picker.currentYear)
        }
        if (typeof this.$refs.picker.currentMonth === 'number') {
          base.setMonth(this.$refs.picker.currentMonth)
        }
        if (typeof this.$refs.picker.currentHour === 'number' && hour === undefined) {
          base.setHours(this.$refs.picker.currentHour)
        }
        if (typeof this.$refs.picker.currentMinute === 'number' && minute === undefined) {
          base.setMinutes(this.$refs.picker.currentMinute)
        }
      }

      if (hour !== undefined) {
        base.setHours(hour)
      }
      if (minute !== undefined) {
        base.setMinutes(minute)
      }
      base.setSeconds(0)
      base.setMilliseconds(0)

      this.onModelUpdate(base, { preserveClock: false })
    }
  }
}
</script>

<style scoped>
.date-picker {
  display: block;
  width: 100%;
}

.date-picker :deep(.p-datepicker) {
  display: flex;
  width: 100%;
}

.date-picker :deep(.p-inputtext) {
  flex: 1 1 auto;
  min-width: 0;
}

.date-picker :deep(.p-datepicker-input-icon-container) {
  display: flex;
  align-items: center;
  cursor: pointer;
  z-index: 1;
}

.date-picker :deep(.p-datepicker-input-icon-trigger) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0.25rem;
  margin: -0.25rem;
}

.date-picker :deep(.p-datepicker-input-icon) {
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  width: 1rem;
  height: 1rem;
}
</style>

<!-- Panel is teleported to body, so these styles cannot be scoped. -->
<style>
.wsd-datepicker-panel.p-datepicker-panel .p-datepicker-time-picker {
  display: none !important;
}

.wsd-datepicker-panel .wsd-time-select {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 0.85rem 0.9rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

.wsd-datepicker-panel .wsd-time-select__display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}

.wsd-datepicker-panel .wsd-time-select__part {
  min-width: 3.25rem;
  padding: 0.35rem 0.55rem;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.4rem;
  background: #fff;
  color: inherit;
  font-size: 1.25rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
  cursor: pointer;
}

.wsd-datepicker-panel .wsd-time-select__part:hover {
  border-color: var(--primary, #0d6efd);
}

.wsd-datepicker-panel .wsd-time-select__part.is-active {
  border-color: var(--primary, #0d6efd);
  background: var(--primary, #0d6efd);
  color: #fff;
}

.wsd-datepicker-panel .wsd-time-select__sep {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1;
}

.wsd-datepicker-panel .wsd-time-select__grid {
  display: grid;
  gap: 0.35rem;
  max-height: 10.5rem;
  overflow: auto;
  padding: 0.15rem;
}

.wsd-datepicker-panel .wsd-time-select__grid--hours {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.wsd-datepicker-panel .wsd-time-select__grid--minutes {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.wsd-datepicker-panel .wsd-time-select__option {
  min-height: 2.1rem;
  border: 1px solid transparent;
  border-radius: 0.35rem;
  background: rgba(0, 0, 0, 0.04);
  color: inherit;
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}

.wsd-datepicker-panel .wsd-time-select__option:hover {
  border-color: rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.08);
}

.wsd-datepicker-panel .wsd-time-select__option.is-selected {
  border-color: var(--primary, #0d6efd);
  background: var(--primary, #0d6efd);
  color: #fff;
}

.wsd-datepicker-panel.p-datepicker-timeonly .wsd-time-select {
  border-top: 0;
  min-width: 16rem;
}
</style>
